<template>
  <div
    ref="videoWrapper"
    :class="[isHero ? 'is-hero absolute inset-0 size-full' : 'relative']"
  >
    <div
      class="size-full transition-opacity opacity-0"
      :class="[
        { 'opacity-100': videoLoaded },
        isHero ? 'absolute inset-0' : 'relative top-0 left-0',
      ]"
      :aria-hidden="videoAriaHidden"
      :aria-label="$texts('accessibility.video_player')"
    >
      <video
        ref="videoPlayer"
        playsinline
        class="size-full object-cover"
        :class="[
          videoClass,
          isHero ? 'absolute inset-0 size-full' : 'relative top-0 left-0',
        ]"
        preload="none"
        :poster="poster"
        @click="togglePlayPause"
      />
    </div>

    <div v-if="isLanding">
      <button
        class="size-full absolute top-0 left-0 z-30"
        @click="togglePlayPause"
      >
        <span class="sr-only">
          {{ videoButtonText }}
        </span>
      </button>
      <div
        class="bg-black/50 text-lightGreen hover:bg-violet absolute px-4 py-2 rounded-full z-20 right-7 bottom-4 text-lg font-regular flex flex-row items-center"
      >
        <div :class="{ paused: isPaused }" class="video-player__control mr-3" />
        <span v-if="isPaused">
          {{ $texts('hero.playLabel') }}
        </span>
        <span v-else> {{ $texts('hero.pauseLabel') }}</span>
      </div>
    </div>
    <button
      v-else
      :class="[
        'absolute top-0 left-0 z-30 w-full cursor-pointer flex items-center justify-center',
        { hidden: !isPaused },
        isHero ? 'inset-0 size-full' : 'aspect-video size-full',
      ]"
      :aria-label="$texts('video.load')"
      @click="togglePlayPause"
    >
      <img
        v-if="poster"
        :src="poster"
        :class="[
          'absolute top-0 left-0 size-full object-cover',
          isHero ? 'inset-0 size-full' : '',
        ]"
      />

      <SpriteSymbol name="play" class="size-[75px] z-10" />
    </button>
  </div>
</template>

<script setup lang="ts">
// Types are declared in declarations.d.ts
import type { DefineImageStyleConfig } from '#rokka/types'
type Hls = import('hls.js').default
type ErrorData = import('hls.js').ErrorData
const { $texts } = useNuxtApp()

interface VideoPlayerProps {
  autoplay?: boolean
  controls?: boolean
  loop?: boolean
  sources?: {
    src: string
    type?: string
  }[]
  muted?: boolean
  videoAriaHidden?: boolean
  isLanding?: boolean
  poster?: string
  imageStyle?: DefineImageStyleConfig
  isHero?: boolean
  videoClass?: string
  playsinline?: boolean
}

const props = withDefaults(defineProps<VideoPlayerProps>(), {
  autoplay: false,
  playsinline: true,
  controls: true,
  loop: false,
  sources: undefined,
  muted: false,
  videoAriaHidden: false,
  isLanding: false,
  isHero: false,
  poster: '',
  videoClass: '',
})

const videoWrapper = ref<HTMLElement | null>(null)
const videoPlayer = ref<HTMLVideoElement | null>(null)
const hls = ref<Hls | null>(null)
const isPaused = ref(!props.autoplay)
const videoLoaded = ref(false) // Used to show the video when it's loaded

const videoButtonText = computed(() => {
  return isPaused.value
    ? $texts('accessibility.video_is_paused')
    : $texts('accessibility.video_is_running')
})

const togglePlayPause = () => {
  if (videoPlayer.value) {
    if (videoPlayer.value.paused) {
      videoPlayer.value.play()
      isPaused.value = false
    } else {
      videoPlayer.value.pause()
      isPaused.value = true
    }
  }
}

// Initialize HLS only when video comes into view
const loadHls = async () => {
  const { default: Hls } = await import('hls.js')

  if (
    Hls.isSupported() &&
    videoPlayer.value &&
    props.sources &&
    props.sources[0]?.src
  ) {
    hls.value = new Hls({
      capLevelToPlayerSize: true,
      maxDevicePixelRatio: 2,
      maxBufferLength: 6,
      maxBufferSize: 0, // this is overridden by maxMaxBufferLength
    })
    if (!hls.value || !videoPlayer.value) return
    hls.value.loadSource(props.sources[0]!.src)
    hls.value.attachMedia(videoPlayer.value)

    hls.value?.on(Hls.Events.MANIFEST_PARSED, () => {
      videoLoaded.value = true // Set loaded flag for fade-in
      // in theory not exposed by hls.js, but it works
      if (!hls.value) return
      const capLevelController = (hls.value as any)?.capLevelController
      const levels = hls.value.levels
      if (!capLevelController || !levels) {
        console.error('Hls – capLevelController or levels not available')
        return
      }

      const maxLevel = capLevelController.getMaxLevel(levels.length - 1)

      if (hls.value && maxLevel !== -1) {
        hls.value.startLevel = maxLevel
      } else {
        console.error('Hls – maxLevel not available')
      }
    })

    hls.value?.on(Hls.Events.ERROR, function (_: unknown, data: ErrorData) {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.MEDIA_ERROR:
            console.log('Hls – fatal media error encountered, try to recover')
            hls.value?.recoverMediaError()
            break
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.error('Hls – fatal network error encountered', data)
            // All retries and media options have been exhausted.
            // Immediately trying to restart loading could cause loop loading.
            // Consider modifying loading policies to best fit your asset and network
            // conditions (manifestLoadPolicy, playlistLoadPolicy, fragLoadPolicy).
            break
          default:
            console.error('Hls – cannot recover from error', data)
            // cannot recover
            hls.value?.destroy()
            break
        }
      }
    })
  } else if (
    videoPlayer.value &&
    videoPlayer.value.canPlayType('application/vnd.apple.mpegurl')
  ) {
    // Native HLS support (Safari)
    videoPlayer.value.src = props.sources![0]!.src
    videoLoaded.value = true // Set loaded flag for fade-in
  }

  if (videoPlayer.value) {
    videoPlayer.value.controls = props.controls
    videoPlayer.value.autoplay = props.autoplay
    videoPlayer.value.loop = props.loop
    videoPlayer.value.muted = props.muted

    if (props.autoplay) {
      videoPlayer.value.play()
      isPaused.value = false
    }
  }
}

const play = () => {
  videoPlayer.value?.play()
  isPaused.value = false
}

// Watch for source changes and reload HLS
watch(
  () => props.sources,
  (newSources, oldSources) => {
    if (
      import.meta.client &&
      oldSources?.length &&
      newSources?.length &&
      oldSources.some(
        (oldSrc) => !newSources.some((newSrc) => oldSrc.src === newSrc.src),
      )
    ) {
      // Source changed, destroy old HLS instance and reload
      hls.value?.destroy()
      hls.value = null
      videoLoaded.value = false
      isPaused.value = !props.autoplay
      loadHls()
    }
  },
)

// Watch for prop changes and update video element
watch(
  () => [props.controls, props.muted, props.loop, props.autoplay],
  () => {
    if (videoPlayer.value) {
      videoPlayer.value.controls = props.controls
      videoPlayer.value.muted = props.muted
      videoPlayer.value.loop = props.loop
      if (props.autoplay && videoPlayer.value.paused) {
        videoPlayer.value.play()
        isPaused.value = false
      }
    }
  },
)

defineExpose({
  play,
})

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    if (entries[0]?.isIntersecting) {
      observer.disconnect()
      loadHls() // Load HLS when the video comes into view
    }
  })
  if (videoWrapper.value) {
    observer.observe(videoWrapper.value)
  }
})

onBeforeUnmount(() => {
  hls.value?.destroy()
})

defineOptions({
  name: 'MediaVideoPlayer',
})
</script>

<style>
@reference "~/assets/css/tailwind.css";

.is-hero {
  @apply absolute inset-0 size-full;

  video {
    @apply absolute inset-0 w-full h-full;
    object-fit: cover;
    min-width: 100%;
    min-height: 100%;
  }

  /* Fallback poster image for non-landing videos */
  img {
    @apply absolute inset-0 w-full h-full;
    object-fit: cover;
    min-width: 100%;
    min-height: 100%;
  }
}

div:not(.is-hero) {
  video {
    width: 100%;
    height: auto;
    position: relative;
  }
}

.video-player__control {
  --base-size: 10px;

  @media (width >= theme(--breakpoint-lg)) {
    --base-size: 15px;
  }

  border: 0;
  background: transparent;
  box-sizing: border-box;
  width: 0;
  height: calc(var(--base-size) * 1);
  border-color: transparent transparent transparent var(--color-lightGreen);
  transition: 100ms all ease;
  cursor: pointer;
  pointer-events: none;

  /* play state */
  border-style: double;
  border-width: 0px 0 0px calc(var(--base-size) * 0.866);

  &.paused {
    border-style: solid;
    border-width: calc(var(--base-size) * 0.5) 0 calc(var(--base-size) * 0.5)
      calc(var(--base-size) * 0.866);
  }

  &:hover {
    border-color: transparent transparent transparent var(--color-lightGreen);
  }
}
</style>
